interface Reason {
  icon: string;
  title: string;
  desc: string;
}

interface InnerWhyChooseUsProps {
  label?: string;
  h2: string;
  desc?: string;
  reasons: Reason[];
  images: string[];
}

export default function InnerWhyChooseUs({ label, h2, desc, reasons, images }: InnerWhyChooseUsProps) {
  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-gold"></span>
              <p className="text-gold text-xs font-bold uppercase tracking-[0.18em]">
                {label ?? 'Our Difference'}
              </p>
            </div>
            <h2 className="font-heading text-navy font-bold mb-5 leading-tight" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 3.2rem)' }}>
              {h2}
            </h2>
            {desc && (
              <p className="text-gray-500 text-base leading-relaxed mb-9 max-w-md">{desc}</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {reasons.map((reason) => (
                <div
                  key={reason.title}
                  className="flex items-start gap-4 group p-4 rounded-xl hover:bg-offWhite transition-colors border border-transparent hover:border-gray-100"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-sage/15 flex-shrink-0 mt-0.5 group-hover:bg-sage/25 transition-colors">
                    <i className={`${reason.icon} text-sage text-lg`}></i>
                  </div>
                  <div>
                    <p className="font-bold text-navy text-sm mb-1 leading-tight">{reason.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{reason.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right image collage */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {images.slice(0, 4).map((img, i) => (
                <div
                  key={i}
                  className={`rounded-2xl overflow-hidden w-full ${
                    i === 0 ? 'h-60' : i === 1 ? 'h-44 mt-10' : i === 2 ? 'h-44 -mt-5' : 'h-60 mt-4'
                  }`}
                >
                  <img
                    src={img}
                    alt={`Senior care ${i + 1}`}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
            {/* Floating badge */}
            <div
              className="absolute bottom-5 left-5 bg-navy text-white rounded-2xl px-6 py-4"
              style={{ boxShadow: '0 8px 30px rgba(15,42,68,0.35)' }}
            >
              <div className="flex items-center gap-0.5 mb-1.5">
                {[1,2,3,4,5].map((s) => (
                  <i key={s} className="ri-star-fill text-gold text-sm"></i>
                ))}
              </div>
              <p className="text-white font-bold text-sm leading-tight">5-Star Rated Care</p>
              <p className="text-white/45 text-xs mt-0.5">Oakland, IL &amp; East-Central Illinois</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
